import React from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  VStack,
  Box,
  Flex,
  Text,
  Badge,
  Button,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { Bell, CheckCircle, AlertTriangle, Info, Trash2 } from 'lucide-react';
import { useNotification } from '../hooks/useNotification';

export const NotificationsDrawer = ({ isOpen, onClose }) => {
  const { notifications, markAllAsRead, removeNotification } = useNotification();

  const getIcon = (type) => {
    if (type === 'success') return <Icon as={CheckCircle} color="#00C853" size={18} />;
    if (type === 'warning') return <Icon as={AlertTriangle} color="#FFC107" size={18} />;
    return <Icon as={Info} color="#6C63FF" size={18} />;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent _dark={{ bg: '#161F30' }}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" display="flex" justify="space-between" align="center">
          <Flex align="center" gap={2}>
            <Bell size={18} color="#FF4D6D" />
            <Text fontWeight="800" fontSize="md">Notifications</Text>
            {unreadCount > 0 && (
              <Badge colorScheme="red" borderRadius="full" px={2} fontSize="10px">
                {unreadCount} New
              </Badge>
            )}
          </Flex>
          <Button size="xs" variant="ghost" colorScheme="pink" onClick={markAllAsRead}>
            Mark all read
          </Button>
        </DrawerHeader>

        <DrawerBody p={4}>
          {notifications.length === 0 ? (
            <Flex direction="column" align="center" justify="center" h="200px" color="gray.400">
              <Bell size={32} />
              <Text fontSize="sm" mt={2}>No notifications</Text>
            </Flex>
          ) : (
            <VStack spacing={3} align="stretch">
              {notifications.map((n) => (
                <Box
                  key={n.id}
                  p={3.5}
                  borderRadius="14px"
                  bg={n.read ? 'transparent' : 'pink.50'}
                  _dark={{ bg: n.read ? 'rgba(255,255,255,0.02)' : 'rgba(255,77,109,0.1)' }}
                  border="1px solid"
                  borderColor={n.read ? 'gray.100' : 'pink.200'}
                  _dark={{ borderColor: n.read ? 'gray.800' : 'pink.900' }}
                >
                  <Flex align="start" gap={3}>
                    {getIcon(n.type)}
                    <Box flex="1">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="xs" fontWeight="bold">
                          {n.title}
                        </Text>
                        <IconButton
                          icon={<Trash2 size={12} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          onClick={() => removeNotification(n.id)}
                          aria-label="Remove notification"
                        />
                      </Flex>
                      <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }} mt={1}>
                        {n.message}
                      </Text>
                      <Text fontSize="10px" color="gray.400" mt={1}>
                        {n.time}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
